import {
    BusinessUnitInterface,
    BusinessUnitModel,
} from './business-unit.model';
import { BaseModel } from '@app/core/models/base.model';
import { Exclude, Expose, Transform, Type } from 'class-transformer';
import {
    OrganizationMetadataInterface,
    OrganizationMetadataModel,
} from './metadata.model';
import { DateTime } from 'luxon';
import { SubscriptionType } from '@app/modules/organization/constants/organization.constant';
import { DateMoment } from '@app/core/common/date-moment';
import { Helper } from '@app/core/common/helper';
import { SubscriptionOfOrgModel } from './subscription.model';
import { UserModel } from './user.model';

export interface OrgSubscriptionInfoInterface {
    startDate: DateTime | string;
    endDate: DateTime | string;
    type: SubscriptionType;
}

export class OrgSubscriptionInfoModel
    extends BaseModel
    implements OrgSubscriptionInfoInterface {
    @Transform((val) => (val ? DateMoment.date(val) : val))
    @Transform(
        (val) =>
            val && DateMoment.isMoment(val) ? (val as DateTime).toISO() : val,
        { toPlainOnly: true }
    )
    startDate: DateTime;

    @Transform(
        (val) =>
            val && DateMoment.isMoment(val) ? (val as DateTime).toISO() : val,
        { toPlainOnly: true }
    )
    @Transform(
        (val, obj) => {
            const startDate: DateTime = DateMoment.date(obj.startDate);
            let endDate = val;

            if (!startDate.isValid) {
                return null;
            }

            switch (obj.type) {
                case SubscriptionType.MONTHLY: {
                    endDate = startDate.plus({ months: 1 });
                    break;
                }
                case SubscriptionType.YEARLY: {
                    endDate = startDate.plus({ years: 1 });
                    break;
                }
                default: {
                    endDate = DateMoment.date(val);
                }
            }

            return endDate;
        },
    )
    endDate: DateTime;
    type: SubscriptionType;

    @Expose()
    @Exclude({ toPlainOnly: true })
    get isExpired(): boolean {
        return DateMoment.current() > DateMoment.date(this.endDate);
    }

    @Expose()
    @Exclude({ toPlainOnly: true })
    get remainingDays(): number {

        if (this.endDate) {
            const days = DateMoment.dateDiff(DateMoment.current(), this.endDate, true, 'days');

            return days < 0 ? 0 : Math.round(days);
        }

        return 0;
    }

    @Expose()
    @Exclude({ toPlainOnly: true })
    get totalSubcriptionDays(): number {
        if (this.endDate && this.startDate) {
            return Math.round(DateMoment.dateDiff(this.startDate, this.endDate, true, 'days'));
        }

        return 0;
    }

    @Expose()
    @Exclude({ toPlainOnly: true })
    get percentUsage() {
        if (this.isExpired) {
            return 100;
        }

        const percent = (1 - (this.remainingDays / this.totalSubcriptionDays)) * 100;

        return Helper.round(percent);
    }
}

export interface OrganizationInterface {
    id: number;
    name: string;
    status: string;
    logo: string;
    favicon: string;
    email: string;
    address: string;
    phone: string;
    website: string;
    maximumBusinessUnit: number;
    ownerId: number;
    businessUnits: BusinessUnitInterface[];
    metadata?: OrganizationMetadataInterface;
    settings: any;
}

export interface AdminOrganizationInterface extends OrganizationInterface {
    subscriptions: SubscriptionOfOrgModel[];
    mainSubscription: SubscriptionOfOrgModel;
}

export class AdminOrganizationModel
    extends BaseModel
    implements AdminOrganizationInterface {
    @Expose()
    id: number;
    @Expose()
    name: string;
    @Expose()
    status: string;
    @Expose()
    logo: string;
    @Expose()
    favicon: string;
    @Expose()
    email: string;
    @Expose()
    address: string;
    @Expose()
    phone: string;
    @Expose()
    website: string;
    @Expose()
    maximumBusinessUnit: number;
    @Expose()
    ownerId: number;
    @Expose()
    @Type(() => BusinessUnitModel)
    businessUnits: BusinessUnitModel[];
    @Expose()
    @Type(() => OrganizationMetadataModel)
    metadata?: OrganizationMetadataModel;

    settings: any;

    @Type(() => SubscriptionOfOrgModel)
    subscriptions: SubscriptionOfOrgModel[] = [];

    @Type(() => UserModel)
    owner: UserModel;

    @Exclude({ toPlainOnly: true })
    get mainSubscription(): SubscriptionOfOrgModel | undefined {
        return this.subscriptions[0];
    }

    /**
     * The business unit default of organization. That is the first business unit of organization
     */
    get defaultBusinessUnit(): BusinessUnitModel | undefined {
        return this.businessUnits?.[0];
    }
}

export class OrganizationModel
    extends BaseModel
    implements OrganizationInterface {
    @Expose()
    id: number;
    @Expose()
    name: string;
    @Expose()
    status: string;
    @Expose()
    logo: string;
    @Expose()
    favicon: string;
    @Expose()
    email: string;
    @Expose()
    address: string;
    @Expose()
    phone: string;
    @Expose()
    website: string;
    @Expose()
    maximumBusinessUnit: number;
    @Expose()
    ownerId: number;
    @Expose()
    @Type(() => BusinessUnitModel)
    businessUnits: BusinessUnitModel[];
    @Expose()
    @Type(() => OrganizationMetadataModel)
    metadata: OrganizationMetadataModel;

    settings: any;
}
